/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";
import type { DomainToken } from "@tivocorp/data-domain-lib";

import {
  extractServiceErrorMessage,
  localizedErrorFallback
} from "../../../model/utility";
import { LocaleKeys } from "../../../model/locale";
import { Log } from "../../../model/logging";
import {
  SignInFlow,
  SignInFlowEvent,
  SignInLogger,
  SignInResult
} from "../../../model/logging/SignInLogger";
import { TokenManager } from "../../../model/auth";
import type { TAuthContext } from "../../../model/auth";
import { DiagnosticSeverity } from "../../../model/types";

const TAG: string = "signIn/tokenThunks";

const REHYDRATED_MESSAGE: string = "Session restored from persisted token";

/** Options for {@link rehydrateTokenFromStorage}. */
export interface TRehydrateTokenOptions {
  /**
   * Settles once the TSRI endpoint catalog has loaded (the dispatched
   * `fetchGlobalEndpoints` promise). Awaited only when the restored token
   * must be refreshed.
   */
  endpointsReady?: Promise<unknown>;
}

/**
 * Emits the spec's `signIn` analytics event for a returning device.
 *
 * Session restore produces the same terminal state as an interactive sign-in,
 * so omitting it here would drop the majority of the fleet out of the
 * success-rate denominator. It goes straight to the logger rather than
 * through the sign-in timeline: there is no multi-step flow to time.
 */
function logSessionRestoreAnalytics(durationMs: number): void {
  SignInLogger.logSignInAnalytics({
    status: SignInResult.Success,
    // No spec login_type describes a silent token restore.
    auth_type: SignInFlow.SessionRestore,
    user_initiated_auth: "false",
    is_first_sign_in: "false",
    duration_ms: String(durationMs)
  });
}

/** `outcome` values on token_rehydrated diagnostics. */
const REHYDRATE_OUTCOME_VALID: string = "valid";
const REHYDRATE_OUTCOME_REFRESHED: string = "refreshed";
const REHYDRATE_OUTCOME_REFRESH_FAILED: string = "refresh_failed";
const REHYDRATE_OUTCOME_UNAUTHENTICATED: string = "unauthenticated";

/**
 * Reports a restored session — served straight from storage, or refreshed on
 * the way out. Both are sign-in outcomes and both belong in the fleet-wide
 * success rate, so they emit the same trio of events under different
 * `outcome` values.
 */
function logRestoreSucceeded(outcome: string, startedAt: number): void {
  const durationMs: number = Date.now() - startedAt;
  SignInLogger.logFlowEvent(
    SignInFlowEvent.TokenRehydrated,
    REHYDRATED_MESSAGE,
    DiagnosticSeverity.Info,
    {
      flow: SignInFlow.SessionRestore,
      outcome: outcome,
      duration_ms: String(durationMs)
    }
  );
  SignInLogger.logSessionRestored({
    outcome: outcome,
    duration_ms: String(durationMs)
  });
  logSessionRestoreAnalytics(durationMs);
}

/**
 * Reports a device with no session to restore. Not a failure: a device that
 * has never signed in (or was signed out) simply has nothing to rehydrate and hands over to the sign-in orchestration.
 */
function logNoSessionToRestore(): void {
  Log.d(TAG, "rehydrateTokenFromStorage() no session on this device");
  SignInLogger.logFlowEvent(
    SignInFlowEvent.TokenRehydrated,
    REHYDRATED_MESSAGE,
    DiagnosticSeverity.Info,
    {
      flow: SignInFlow.SessionRestore,
      outcome: REHYDRATE_OUTCOME_UNAUTHENTICATED
    }
  );
}

/** Reports a session that existed but that no refresh path could renew. */
function logRestoreFailed(startedAt: number, error: unknown): void {
  SignInLogger.logFlowEvent(
    SignInFlowEvent.TokenRehydrateFailed,
    "Silent domain-token refresh failed",
    DiagnosticSeverity.Warning,
    {
      flow: SignInFlow.SessionRestore,
      outcome: REHYDRATE_OUTCOME_REFRESH_FAILED,
      duration_ms: String(Date.now() - startedAt)
    }
  );
  Log.e(TAG, "rehydrateTokenFromStorage() silent refresh failed", error);
}

/**
 * Rehydrates auth state on app boot.
 *
 * Delegates to `TokenManager.restore()`, which hydrates the account context
 * from the token store — migrating the pre-consolidation
 * `accessTokenWithExpiry` record on the way when that is all this device has.
 * A restored token inside the expiry skew window is refreshed on the way out,
 * so a returning device still comes back signed in.
 *
 * **This thunk only ever restores; it never signs a device in.** When `restore`
 * finds no token it reports "no session" and stops, rather than asking
 * TokenManager for one. Asking would run the whole account re-authentication
 * walk — auth-config, `/bind`, `/domainToken` — against a device that has
 * nothing to re-authenticate with, and it would run it here, in parallel with
 * `fetchGlobalEndpoints`: the first of those calls resolves a *critical* TSRI
 * service against a catalog that has not loaded yet, which raises the
 * "temporary service connection issue" overlay for the length of the recovery
 * re-fetch. Signing in is `resolveNextAuthStep`'s job, and it runs after the
 * catalog is ready.
 *
 * Resolves to the account token, or null when there is no session to restore —
 * including when a restorable token could not be renewed, since the sign-in
 * orchestration is still free to recover and a boot-time alert would pre-empt
 * it. Rejects only on an unexpected failure of the restore itself.
 *
 * **A stale token waits for the endpoint catalog.** Refreshing calls DTS /
 * IAS / DBS, whose URLs come from the in-memory TSRI catalog — empty on every
 * cold start and every browser reload until `fetchGlobalEndpoints` lands.
 * Refreshing before then made each call trigger its own catalog recovery
 * (with the connection-issue overlay), and a recovery that gave up failed the
 * refresh and sent a signed-in user back to the sign-in screen. So when the
 * restored token needs renewing, this awaits `endpointsReady` first. A fresh
 * token needs no network and is served without waiting. The route guards
 * render nothing while `isTokenRehydrating` is true, so the wait never shows
 * the sign-in screen.
 *
 * `signIn.domainToken` is only the route guards' "is there a session" marker:
 * the token itself is read from TokenManager at each call site, never from
 * Redux, so this value is never used as a credential.
 */
export const rehydrateTokenFromStorage = createAsyncThunk<
  DomainToken | null,
  TRehydrateTokenOptions | undefined,
  { rejectValue: string }
>(
  "signIn/rehydrateTokenFromStorage",
  async (options: TRehydrateTokenOptions | undefined, { rejectWithValue }) => {
    const rehydrateStartTime: number = Date.now();
    try {
      Log.d(TAG, "rehydrateTokenFromStorage() restoring via TokenManager");
      await TokenManager.restore();
    } catch (error: unknown) {
      Log.e(TAG, "rehydrateTokenFromStorage()", error);
      logRestoreFailed(rehydrateStartTime, error);
      return rejectWithValue(
        extractServiceErrorMessage(
          error,
          localizedErrorFallback(
            LocaleKeys.SIGN_IN_SESSION_RESTORE_ERROR_MESSAGE
          )
        )
      );
    }

    if (!TokenManager.hasToken(null)) {
      logNoSessionToRestore();
      return null;
    }

    // Sampled before the token is requested: afterwards a refresh would
    // already have happened and "valid" could not be told from "refreshed".
    const wasAlreadyValid: boolean = TokenManager.hasFreshToken(null);

    if (
      !wasAlreadyValid &&
      options !== undefined &&
      options.endpointsReady !== undefined
    ) {
      try {
        await options.endpointsReady;
      } catch (error: unknown) {
        // The endpoint fetch owns its own failure surface. Try the refresh
        // anyway: it resolves or fails on its own terms.
        Log.w(TAG, "rehydrateTokenFromStorage() endpoints not ready", error);
      }
    }

    try {
      const authContext: TAuthContext = await TokenManager.getAuthContext(null);
      logRestoreSucceeded(
        wasAlreadyValid ? REHYDRATE_OUTCOME_VALID : REHYDRATE_OUTCOME_REFRESHED,
        rehydrateStartTime
      );
      return {
        token: authContext.accessToken,
        domain: authContext.domain,
        expiration: authContext.expiration
      };
    } catch (error: unknown) {
      // A token existed but no refresh path could renew it. The device has to
      // authenticate again, which the sign-in orchestration handles — so this
      // resolves to "no session" rather than surfacing an error over a screen
      // the user is about to be moved off anyway.
      logRestoreFailed(rehydrateStartTime, error);
      return null;
    }
  }
);
